import { jwtDecode } from "jwt-decode";
import { useCallback, useEffect, useState, useRef } from "react";
import { useIdleTimer } from "react-idle-timer";
import { setAccessToken as setReduxAccessToken } from "reducers/main";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";

import type { AppStore } from "services/store";
import { getSessionId, logToSentry } from "utils/sentry";
import { useRecordLoginMutation } from "services/analytics-api";

import {
  DEFAULT_ACCESS_TOKEN_REFRESH_INTERVAL_MS,
  DEFAULT_INACTIVITY_TIMEOUT_MS,
} from "constants/AuthConstants";
import { logoutApp } from "api-helpers/authUtils";
import {
  LOGIN_TRIGGER_INITIAL_MOUNT,
  SESSION_GUARD_TOKEN_KEY,
  acquireLoginRecordingLock,
  clearRecordedLoginInSession,
  getBackoffDelay,
  hasActiveLoginRecordingLock,
  isAuthRefreshError,
  isLoginRecordedOrInProgress,
  isNetworkRefreshError,
  releaseLoginRecordingLock,
  setHasRecordedLoginInSession,
  setLoginRecordingPendingInSession,
  shouldLogoutAfterVisibilityRefreshFailure,
  shouldRecordLoginAnalytics,
  shouldRefreshTokenOnVisibilityChange,
  VISIBILITY_STALE_THRESHOLD_MS,
} from "./authSessionUtils";

const clearSessionGuardToken = () => {
  sessionStorage.removeItem(SESSION_GUARD_TOKEN_KEY);
  localStorage.removeItem(SESSION_GUARD_TOKEN_KEY);
};

const clearLogoutSessionGuards = () => {
  clearRecordedLoginInSession();
  clearSessionGuardToken();
};

// auth flow debugging helpers
const logAuthEvent = (event: string, data?: Record<string, unknown>) => {
  // only log in development or if explicitly set to 'dev' env
  const isDev = import.meta.env.DEV || import.meta.env.VITE_APP_ENV === "dev";
  if (!isDev) return;

  const timestamp = new Date().toISOString();
  // eslint-disable-next-line no-console
  console.log(`[AUTH ${timestamp}] ${event}`, data ?? "");
};

// Network connectivity check
const isNetworkConnected = () => {
  return (
    navigator.onLine &&
    (
      window as unknown as {
        navigator: { connection: { effectiveType: string } };
      }
    ).navigator?.connection?.effectiveType !== "none"
  );
};

// Exponential backoff for token refresh retries
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Token refresh with retry logic
const refreshTokenWithRetry = async (
  getAccessTokenSilently: () => Promise<string>,
  maxAttempts: number = 3,
): Promise<string | null> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      if (!isNetworkConnected()) {
        logAuthEvent("Token refresh skipped - no network connection", {
          attempt: attempt + 1,
        });
        throw new Error("No network connection");
      }

      const token = await getAccessTokenSilently();
      if (attempt > 0) {
        logAuthEvent("Token refresh succeeded after retry", {
          attempt: attempt + 1,
        });
      }
      return token;
    } catch (error) {
      const isLastAttempt = attempt === maxAttempts - 1;
      const delay = getBackoffDelay(attempt);

      logAuthEvent(`Token refresh attempt ${attempt + 1} failed`, {
        error: (error as Error).message,
        isLastAttempt,
        nextRetryIn: isLastAttempt ? null : `${delay}ms`,
      });

      if (isLastAttempt) {
        throw error; // Re-throw on final attempt
      }

      // Wait before retry
      await sleep(delay);
    }
  }
  return null;
};

const logTokenInfo = (token: string, source: string) => {
  try {
    const decoded = jwtDecode<{ exp: number; iat: number; sub: string }>(token);
    const now = Math.floor(Date.now() / 1000);
    const expiresIn = decoded.exp - now;
    const expiresAt = new Date(decoded.exp * 1000).toISOString();

    logAuthEvent(`Token from ${source}`, {
      expiresAt,
      expiresInSeconds: expiresIn,
      expiresInMinutes: Math.round(expiresIn / 60),
      isExpired: expiresIn <= 0,
      sub: decoded.sub,
    });
  } catch (e) {
    logAuthEvent(`Failed to decode token from ${source}`, { error: e });
  }
};

type UseAuthSessionOptions = {
  store: AppStore;
  idleTimeoutMs?: number;
  refreshIntervalMs?: number;
};

type UseAuthSessionReturn = {
  accessToken: string | null;
  isAuthenticated: boolean;
};

/**
 * handles authentication session management including token refresh, idle logout, and visibility change.
 * @param store - the Redux store instance.
 * @param idleTimeoutMs - the idle timeout in milliseconds (default: 20 minutes).
 * @param refreshIntervalMs - the refresh interval in milliseconds (default: 19 minutes and 10 seconds).
 * @returns an object containing the access token and authentication status.
 */
export const useAuthSession = ({
  store,
  idleTimeoutMs = DEFAULT_INACTIVITY_TIMEOUT_MS, // 20 minutes default
  refreshIntervalMs = DEFAULT_ACCESS_TOKEN_REFRESH_INTERVAL_MS, // 19 min 10 sec default
}: UseAuthSessionOptions): UseAuthSessionReturn => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const {
    getAccessTokenSilently,
    logout,
    loginWithRedirect,
    isAuthenticated,
    user,
  } = useAuth0();
  const [recordLogin] = useRecordLoginMutation();
  const hasRecordedLogin = useRef(false);

  // Identify the current user to Sentry so every breadcrumb / log / error
  // event carries their id+email. Without this, the only correlation between
  // a Sentry event and a person is uiSessionId — an opaque per-browser UUID
  // that can't be reverse-looked-up from a user complaint. With it, finding
  // a user's full auth timeline in Sentry is a single filter on user.email.
  // `sendDefaultPii: true` is already set on the Sentry init in utils/sentry,
  // so the email/username fields are actually forwarded.
  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({
        id: user.sub,
        email: user.email,
        username: user.email,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [isAuthenticated, user]);

  const syncTokenToState = useCallback(
    (token: string, source: string) => {
      setAccessToken(token);
      store.dispatch(setReduxAccessToken(token));
      logTokenInfo(token, source);
    },
    [store],
  );

  const recordLoginAnalyticsIfNeeded = useCallback(async () => {
    if (
      isLoginRecordedOrInProgress({
        hasRecordedLoginRef: hasRecordedLogin.current,
      }) ||
      hasActiveLoginRecordingLock()
    ) {
      return;
    }

    const lockOwner = acquireLoginRecordingLock();
    if (!lockOwner) {
      return;
    }

    try {
      // Re-check after winning the lock: another tab may have recorded while we
      // were racing for it.
      if (
        isLoginRecordedOrInProgress({
          hasRecordedLoginRef: hasRecordedLogin.current,
        })
      ) {
        return;
      }

      const loginPayload = {
        meta: {
          trigger: LOGIN_TRIGGER_INITIAL_MOUNT,
          ui_session_id: getSessionId(),
          auth_method: "token_fetch",
          is_authenticated_at_record_time: isAuthenticated,
        },
      };

      setLoginRecordingPendingInSession();
      await recordLogin(loginPayload).unwrap();
      hasRecordedLogin.current = true;
      setHasRecordedLoginInSession();
      logAuthEvent("Login analytics recorded successfully");
    } catch (error) {
      clearRecordedLoginInSession();
      logAuthEvent("Failed to record login analytics", { error });
      // Don't fail the auth flow if analytics fails
    } finally {
      releaseLoginRecordingLock(lockOwner);
    }
  }, [recordLogin, isAuthenticated]);

  // idle timer - logout after inactivity
  const handleOnIdle = useCallback(async () => {
    logAuthEvent("Idle timer fired - preparing inactivity logout", {
      isAuthenticated,
      idleTimeoutMs,
    });
    logToSentry("Idle timeout reached - initiating logout", "info", {
      uiSessionId: getSessionId(),
      reason: "inactivity_timeout",
      idleTimeoutMs,
    });

    // Note: Logout analytics recording has been removed

    clearLogoutSessionGuards();
    if (accessToken) {
      await logoutApp(accessToken);
    }

    if (isAuthenticated) {
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [accessToken, isAuthenticated, logout, idleTimeoutMs]);

  useIdleTimer({
    timeout: idleTimeoutMs,
    onIdle: handleOnIdle,
    // onAction: handleOnAction,
    debounce: 2000,
    disabled: !isAuthenticated,
    crossTab: true,
    syncTimers: 2000,
  });

  // token fetching
  const fetchToken = useCallback(
    async (trigger: string) => {
      logAuthEvent(`Fetching token (trigger: ${trigger})`);
      try {
        const token = await refreshTokenWithRetry(getAccessTokenSilently);
        if (!token) {
          throw new Error("Token refresh failed after retries");
        }

        syncTokenToState(token, trigger);
        logToSentry(`Token fetch SUCCESS (trigger: ${trigger})`, "info", {
          uiSessionId: getSessionId(),
        });

        if (
          shouldRecordLoginAnalytics({
            trigger,
            hasRecordedLoginRef: hasRecordedLogin.current,
          })
        ) {
          await recordLoginAnalyticsIfNeeded();
        }

        return token;
      } catch (e) {
        logAuthEvent(`Token fetch FAILED (trigger: ${trigger})`, {
          error: (e as Error).message,
          errorType: (e as Error).constructor.name,
          isNetworkOnline: navigator.onLine,
        });
        logToSentry(`Token fetch FAILED (trigger: ${trigger})`, "error", {
          uiSessionId: getSessionId(),
          error: (e as Error).message,
          errorType: (e as Error).constructor.name,
        });
        return null;
      }
    },
    [getAccessTokenSilently, syncTokenToState, recordLoginAnalyticsIfNeeded],
  );

  // initial token fetch + periodic refresh
  useEffect(() => {
    fetchToken(LOGIN_TRIGGER_INITIAL_MOUNT);

    const intervalId = setInterval(async () => {
      await fetchToken("periodic refresh");
    }, refreshIntervalMs);

    return () => clearInterval(intervalId);
  }, [fetchToken, refreshIntervalMs]);

  // global forceLogout function (follows window.navigate404 pattern)
  useEffect(() => {
    window.forceLogout = async () => {
      logAuthEvent("Force logout triggered");

      // Note: Logout analytics recording has been removed

      clearLogoutSessionGuards();
      logout({ logoutParams: { returnTo: window.location.origin } });
    };
  }, [logout]);

  // global forceReauth function - silent Auth0 SSO round-trip used in place
  // of a full logout when a 401 indicates the access token is gone but the
  // user's Auth0 tenant session may still be alive. main.tsx's
  // onRedirectCallback restores `returnTo` from localStorage on the way back.
  useEffect(() => {
    window.forceReauth = async () => {
      logAuthEvent("Force reauth triggered");
      logToSentry("Force reauth triggered", "info", {
        uiSessionId: getSessionId(),
        path: window.location.pathname,
      });

      const returnTo = window.location.pathname + window.location.search;
      localStorage.setItem("returnTo", returnTo);
      loginWithRedirect();
    };
  }, [loginWithRedirect]);

  // Expose the Auth0 SDK's getAccessTokenSilently on window so the RTK Query
  // baseQuery's prepareHeaders can fetch a fresh token at request time
  // instead of relying on whatever cached value is in Redux. The SDK
  // already maintains its own in-memory cache and refresh logic, so this
  // is microseconds on cache-hit and one refresh roundtrip on cache-miss.
  // Pinning the source of truth to the SDK eliminates the gap-between-
  // periodic-refreshes class of "Token has expired" 401s entirely.
  //
  // Gated on isAuthenticated so that intake-app users (who authenticate
  // via Django JWT, not Auth0, and have no Auth0 session) don't pay for
  // a guaranteed-to-throw getAccessTokenSilently call on every request.
  // For intake users prepareHeaders sees no global, falls straight
  // through to Redux, and the intake endpoints' own `Intake-Bearer`
  // header override takes effect as before.
  useEffect(() => {
    if (isAuthenticated) {
      window.getFreshAccessToken = getAccessTokenSilently;
    } else {
      window.getFreshAccessToken = undefined;
    }
    return () => {
      window.getFreshAccessToken = undefined;
    };
  }, [getAccessTokenSilently, isAuthenticated]);

  // visibility change handler - refresh token when user returns after being away
  useEffect(() => {
    let hiddenAt: number | null = null;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        logAuthEvent("Tab hidden", {
          hiddenAt: new Date(hiddenAt).toISOString(),
        });
        return;
      }

      // tab is now visible
      const wasHiddenFor = hiddenAt ? Date.now() - hiddenAt : 0;
      logAuthEvent("Tab visible", {
        wasHiddenForMs: wasHiddenFor,
        wasHiddenForMin: Math.round(wasHiddenFor / 60000),
        isAuthenticated,
      });

      if (!isAuthenticated) return;

      // check if token is close to expiring
      let tokenExpiresInSec = Infinity;
      if (accessToken) {
        try {
          const decoded = jwtDecode<{ exp: number }>(accessToken);
          tokenExpiresInSec = decoded.exp - Math.floor(Date.now() / 1000);
        } catch {
          // ignore decode errors
        }
      }

      const shouldRefresh = shouldRefreshTokenOnVisibilityChange({
        wasHiddenForMs: wasHiddenFor,
        tokenExpiresInSec,
      });

      if (!shouldRefresh) {
        logAuthEvent("Skipping refresh - token still fresh", {
          tokenExpiresInSec,
          tokenExpiresInMin: Math.round(tokenExpiresInSec / 60),
        });
        return;
      }

      logAuthEvent("Refreshing token", {
        reason:
          wasHiddenFor > VISIBILITY_STALE_THRESHOLD_MS
            ? "tab was stale"
            : "token expiring soon",
        wasHiddenForMin: Math.round(wasHiddenFor / 60000),
        tokenExpiresInSec,
      });

      try {
        const token = await refreshTokenWithRetry(getAccessTokenSilently);
        if (token) {
          syncTokenToState(token, "visibility change refresh");
        } else {
          throw new Error("Token refresh failed after retries");
        }
      } catch (e) {
        // Be more conservative about logging out - only logout if we're sure it's an auth issue
        // and not just a temporary network issue
        const errorMessage = (e as Error).message;
        const isAuthError = isAuthRefreshError(errorMessage);
        const isNetworkError = isNetworkRefreshError({
          errorMessage,
          isOnline: navigator.onLine,
        });
        const willLogout = shouldLogoutAfterVisibilityRefreshFailure({
          errorMessage,
          isOnline: navigator.onLine,
        });

        logAuthEvent("Token refresh failed on visibility change", {
          error: errorMessage,
          errorType: (e as Error).constructor.name,
          isAuthError,
          isNetworkError,
          isNetworkOnline: navigator.onLine,
          willLogout,
        });

        if (willLogout) {
          logToSentry("Token refresh failed - logging out", "error", {
            uiSessionId: getSessionId(),
            error: errorMessage,
            reason: "auth_error_on_visibility_change",
          });
          clearLogoutSessionGuards();
          logout({ logoutParams: { returnTo: window.location.origin } });
        } else {
          logToSentry(
            "Token refresh failed - keeping session due to network issues",
            "warn",
            {
              uiSessionId: getSessionId(),
              error: errorMessage,
              reason: "network_error_on_visibility_change",
            },
          );
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [
    isAuthenticated,
    accessToken,
    getAccessTokenSilently,
    logout,
    syncTokenToState,
  ]);

  return {
    accessToken,
    isAuthenticated,
  };
};
